


























































































































import { Component, Vue } from 'vue-property-decorator';
import { ProductSystemCodeEntry } from '@/interfaces/ProductSystemCodeEntry';
import { Action } from 'vuex-class';
import { eventBus, eventBusEvents } from '@/helpers/eventBusHelper';
import { ElTable } from 'element-ui/types/table';
import { ElTableColumn } from 'element-ui/types/table-column';
import { ProductGroupTypes } from '@/enums/ProductGroupTypes';

interface RefsInterface {
    multipleTable: ElTable;
    resultsTable: ElTable;
}

interface MultipleSelectionInterface {
    root?: ProductSystemCodeEntry[];
    [code: number]: ProductSystemCodeEntry[];
}

@Component({
    name: 'ProductCodeSearchTable',
})
export default class ProductCodeSearchTable extends Vue {
    public $refs!: Vue['$refs'] & RefsInterface;

    protected parentIndices: number[] = [];
    protected multipleSelection: MultipleSelectionInterface = {};

    @Action('search/searchForResults') protected searchForResults!: ({
        keywords,
        codes,
    }: {
        keywords: string;
        codes: string;
    }) => Promise<unknown>;
    @Action('search/getProductCodeSystemList')
    private getProductCodeSystemList!: (id?: number) => void;

    protected get listItems() {
        return this.$store.getters['search/productCodeSystemList'];
    }

    protected handleSelectionChange(values: ProductSystemCodeEntry[]) {
        this.$set(this.multipleSelection, this.currentSelectionKey, values);
        // set all values of results table to be checked
        this.selectedValues.forEach((selectedRow: ProductSystemCodeEntry) => {
            this.$refs.resultsTable.toggleRowSelection(selectedRow, true);
        });
    }

    protected handleResultsSelectionChange(values: ProductSystemCodeEntry[]) {
        if (!values.length) {
            // unselected all
            this.multipleSelection = {};
            this.$refs.multipleTable.clearSelection();
        }
    }

    protected handleResultsSelect(
        selection: ProductSystemCodeEntry[],
        row: ProductSystemCodeEntry,
    ) {
        if (!selection.includes(row)) {
            // unselect the code that has been unselected from results table
            this.removeSelectedValue(row);
        }
    }

    protected get currentSelectionKey() {
        return this.parentIndices.length
            ? this.parentIndices[this.parentIndices.length - 1]
            : 'root';
    }

    protected get selectedValues(): ProductSystemCodeEntry[] {
        const uniqueValues = new Set(
            Object.values(this.multipleSelection).flat(),
        );
        return Array.from(uniqueValues);
    }

    protected get selectedCodes(): number[] {
        return this.selectedValues.map(
            (value: ProductSystemCodeEntry) => value.code,
        );
    }

    protected removeSelectedValue(value: ProductSystemCodeEntry) {
        const targetKey: any = Object.keys(this.multipleSelection).find(
            (key: any) => {
                const currentEntries: ProductSystemCodeEntry[] = this
                    .multipleSelection[key];
                const codes = currentEntries.map(
                    (val: ProductSystemCodeEntry) => val.code,
                );
                return codes.includes(value.code);
            },
        );
        if (targetKey) {
            this.multipleSelection[targetKey] = this.multipleSelection[
                targetKey
            ].filter((val: ProductSystemCodeEntry) => val.code !== value.code);
        }
        this.$refs.multipleTable.toggleRowSelection(value, false);
    }

    protected addRowClass(row: {
        row: ProductSystemCodeEntry;
        rowIndex: number;
    }) {
        if (+row.row.numOfBrands === 0) {
            return 'el-table__row--empty';
        }
        if (row.row.isLastLevel) {
            return 'el-table__row--last-level';
        }
    }

    protected addRowKey(row: ProductSystemCodeEntry) {
        return row.code;
    }

    protected handleDataChange() {
        const currentValues = this.multipleSelection[this.currentSelectionKey];
        if (currentValues && currentValues.length) {
            // re-select values after changing views
            currentValues.forEach((selectedRow: ProductSystemCodeEntry) => {
                this.$refs.multipleTable.toggleRowSelection(selectedRow, true);
            });
        }
    }

    protected handleRowClick(
        row: ProductSystemCodeEntry,
        column: ElTableColumn,
    ) {
        if (
            column.className === 'number-of-brands' ||
            column.className === 'checkbox'
        ) {
            // clicking on number of brands should be the same as checkbox click
            this.$refs.multipleTable.toggleRowSelection(row);
            return;
        }
        // prevent user from entering already selected entry list
        const isRowSelected =
            this.selectedValues.findIndex(
                (selectedValue: ProductSystemCodeEntry) =>
                    selectedValue.code === row.code,
            ) > -1;

        if (+row.numOfBrands > 0 && !row.isLastLevel && !isRowSelected) {
            this.parentIndices.push(row.id);
            this.getProductCodeSystemList(row.id);
            this.handleDataChange();
        }
    }

    protected handleResultsRowClick(
        row: ProductSystemCodeEntry,
        column: ElTableColumn,
    ) {
        if (
            column.className === 'number-of-brands' ||
            column.className === 'checkbox'
        ) {
            // clicking on number of brands should be the same as checkbox click
            this.$refs.resultsTable.toggleRowSelection(row);
        }
    }

    protected onLevelUp() {
        if (this.currentSelectionKey === 'root') {
            return;
        }
        this.parentIndices.splice(-1, 1);
        const lastId: number | undefined = this.parentIndices[
            this.parentIndices.length - 1
        ];
        this.getProductCodeSystemList(lastId);
        this.handleDataChange();
    }

    protected shouldBeSelectable(row: ProductSystemCodeEntry) {
        if (+row.numOfBrands === 0) {
            return false;
        }
        return true;
    }

    protected shouldArrowBeVisible(row: ProductSystemCodeEntry) {
        return +row.numOfBrands > 1 && !row.isLastLevel;
    }

    protected onCellMouseHover(
        row: ProductSystemCodeEntry,
        column: ElTableColumn,
        cell: HTMLTableCellElement,
    ) {
        const rowElement: HTMLTableRowElement = cell.parentElement! as HTMLTableRowElement;
        rowElement.classList.add('hover');
        cell.classList.add('hover');
        if (
            cell.classList.contains('number-of-brands') ||
            cell.classList.contains('checkbox')
        ) {
            rowElement.classList.add('right-section-hover');
        }
    }

    protected onCellMouseLeave(
        row: ProductSystemCodeEntry,
        column: ElTableColumn,
        cell: HTMLTableCellElement,
    ) {
        const rowElement: HTMLTableRowElement = cell.parentElement! as HTMLTableRowElement;
        cell.classList.remove('hover');
        rowElement.classList.remove('hover');
        rowElement.classList.remove('right-section-hover');
    }

    protected get activeProductGroup() {
        const type = this.$store.getters['user/activeProductGroup'];
        let message = '';
        if (type === ProductGroupTypes.Goodwill) {
            message = 'Goodwill';
        }
        if (type === ProductGroupTypes.Trademark) {
            message = 'Trademark';
        }
        if (type === ProductGroupTypes.CustomerRelations) {
            message = 'Customer relations';
        }
        if (type === ProductGroupTypes.Technology) {
            message = 'Technology';
        }
        if (type === ProductGroupTypes.Software) {
            message = 'Software';
        }
        if (type === ProductGroupTypes.None) {
            message = 'None';
        }
        return message;
    }

    protected mounted() {
        if (this.activeProductGroup === 'None') {
            return;
        }
        this.getProductCodeSystemList();
    }

    protected async onSearch() {
        eventBus.$emit(eventBusEvents.activateGlobalLoading);
        this.$emit('toggle-loading', true);
        const codes = this.selectedValues.map(
            (selection: ProductSystemCodeEntry) => selection.code,
        );
        await this.searchForResults({
            keywords: '',
            codes: codes.join(','),
        }).then(() => {
            eventBus.$emit(eventBusEvents.deactivateGlobalLoading);
        });
        this.$emit('toggle-active-component');
        this.$emit('toggle-loading', false);
    }
}
